/**
 * ConversationThread - Direct-message thread view: header, message list, and composer.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { selectUser } from '../../store/auth.reducer';
import { getMessages, markConversationRead, sendMessage } from '../../helpers/messagesHelpers';
import { getUserDailyPointStats, getAuraMessageCounts } from '../../helpers/auraHelpers';
import { useSocket } from '../../utils/SocketContext';
import { useMessageCount } from '../../utils/MessageCountContext';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import MessageAuraPill from './MessageAuraPill';
import UserAvatar from '../Profile/UserAvatar';

function ConversationThread({ conversationId, otherUser, onBack, onAddPeople }) {
  const currentUser = useSelector(selectUser);
  const { subscribe } = useSocket();
  const { refreshUnreadMessageCount } = useMessageCount();
  const [messages, setMessages] = useState([]);
  const [auraCounts, setAuraCounts] = useState({});
  const [dailyStats, setDailyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Swipe-back only when the thread is rendered inline by the `/messages`
  // route. Inside `MessagesOverlay`, the surrounding `SlideOverlay` already
  // owns the gesture, so `onBack` is omitted there.
  const swipeBackEnabled = typeof onBack === 'function';
  const { ref: panelRef, dragX } = useSwipeToClose({
    open: true,
    direction: 'right',
    onClose: onBack,
    enabled: swipeBackEnabled,
  });
  const dragging = dragX !== 0;
  const panelStyle = dragging ? { transform: `translateX(${dragX}px)` } : undefined;
  const transitionClass = dragging ? 'transition-none' : 'transition-transform duration-300 ease-zen';

  useEffect(() => {
    let cancelled = false;
    getMessages(conversationId)
      .then((data) => {
        if (!cancelled) {
          setMessages(Array.isArray(data) ? data : []);
          refreshUnreadMessageCount();
        }
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [conversationId, refreshUnreadMessageCount]);

  useEffect(() => {
    let cancelled = false;
    getUserDailyPointStats()
      .then((s) => { if (!cancelled) setDailyStats(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const others = messages.filter((m) => !m.is_own);
    if (others.length === 0) {
      setAuraCounts({});
      return;
    }
    let cancelled = false;
    getAuraMessageCounts(others.map((m) => ({ type: 'dm_message', id: m.message_id })))
      .then((counts) => {
        if (!cancelled) setAuraCounts(counts.dm_message || {});
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [messages]);

  useEffect(() => {
    const unsubscribe = subscribe('new_message', (payload) => {
      if (payload.conversation_id === conversationId) {
        const isOwn = payload.message.sender_id === currentUser?.user_id;
        setMessages((prev) => [
          ...prev,
          {
            ...payload.message,
            is_own: isOwn,
            displayname: isOwn ? currentUser?.displayname : otherUser?.displayname,
            username: isOwn ? currentUser?.username : otherUser?.username,
          },
        ]);
        if (!isOwn) {
          markConversationRead(conversationId)
            .then(() => refreshUnreadMessageCount())
            .catch(() => {});
        }
      }
    });
    return unsubscribe;
  }, [subscribe, conversationId, currentUser?.user_id, currentUser?.displayname, currentUser?.username, otherUser?.displayname, otherUser?.username, refreshUnreadMessageCount]);

  useEffect(() => {
    if (scrollRef.current?.scrollIntoView) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const msg = await sendMessage(conversationId, trimmed);
      setMessages((prev) => [
        ...prev,
        {
          ...msg,
          is_own: true,
          displayname: currentUser?.displayname,
          username: currentUser?.username,
        },
      ]);
      setInput('');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      ref={panelRef}
      style={panelStyle}
      className={`flex-1 flex flex-col min-h-0 ${transitionClass}`}
      data-testid="conversation-thread"
    >
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-silver text-sm">Loading...</p>
        </div>
      ) : (
      <>
      <div className="flex-shrink-0 flex items-center gap-3 p-4 border-b border-shadow bg-charcoal">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-ether text-sm hover:underline"
          >
            ← Back
          </button>
        )}
        <UserAvatar
          user={otherUser}
          className="w-10 h-10 rounded-full"
          emojiClassName="text-base"
          alt={otherUser?.displayname || otherUser?.username || 'User avatar'}
        />
        <span className="font-medium text-ash flex-1 truncate">{otherUser?.displayname || otherUser?.username}</span>
        {dailyStats != null && (
          <span className="text-silver text-xs flex-shrink-0" title="Daily aura remaining">
            ✦ {dailyStats.remainingToday}/{dailyStats.dailyLimit}
          </span>
        )}
        {onAddPeople && (
          <button
            type="button"
            onClick={onAddPeople}
            className="text-ether text-sm hover:underline"
            title="Add people to create group chat"
          >
            Add people
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m) => (
          <div
            key={m.message_id}
            className={`flex ${m.is_own ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] flex items-end gap-1 ${m.is_own ? 'flex-row-reverse' : ''}`}
            >
              <div
                className={`px-4 py-2 rounded-2xl ${
                  m.is_own ? 'bg-ether text-void' : 'bg-shadow text-ash'
                }`}
              >
                <p className="text-sm break-words">{m.content}</p>
              </div>
              {!m.is_own && (
                <MessageAuraPill
                  messageId={m.message_id}
                  messageType="dm_message"
                  senderId={m.sender_id}
                  auraCount={auraCounts[m.message_id] ?? 0}
                  onAuraGiven={(points = 1) => {
                    setAuraCounts((prev) => ({
                      ...prev,
                      [m.message_id]: (prev[m.message_id] ?? 0) + points,
                    }));
                    getUserDailyPointStats().then(setDailyStats).catch(() => {});
                  }}
                />
              )}
            </div>
          </div>
        ))}
        <div ref={scrollRef} />
      </div>
      <form onSubmit={handleSend} className="sticky bottom-0 flex-shrink-0 p-4 border-t border-shadow bg-charcoal">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 rounded-xl bg-shadow border border-charcoal text-ash placeholder-silver focus:outline-none focus:ring-2 focus:ring-ether/50"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="px-4 py-3 rounded-xl bg-ether text-void font-medium disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </form>
      </>
      )}
    </div>
  );
}

export default ConversationThread;
